import {
  type BillingMachineResource,
  BillingMachinesController_list_v1,
  type MachineBillResource,
  MachineBillsController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { currentCursor, type CursorTrail, FIRST_PAGE } from '../back-office/cursor-pagination';
import { CursorPager } from '../back-office/CursorPager';
import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { machineBillPath, paths } from '../paths';
import { defaultPeriod, periodProblem, type ReportingPeriod } from '../reporting/reporting-period';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';

/**
 * Bills imported from the billing machines: `GET /v1/machine-bills`.
 *
 * Imported sales never appear in `GET /v1/sales`, so this is where they are
 * found. Keyset paged, so `CursorPager` and never an offset.
 *
 * **Always over a period, unless narrowed to one upload.** The unfiltered count
 * is slow at a year of data (`blossom-rest-api#508`) and filtered reads are
 * fast, so the screen opens on the last thirty trading days — the same default
 * the cash book uses. `?importId=` is the link from an upload's record: an
 * import is already narrow, and its bills may fall on days outside any period
 * somebody would think to pick, so the period is dropped rather than combined.
 */

type ListState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly items: readonly MachineBillResource[];
      readonly total: number;
      readonly nextCursor: string | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The "not filtering" values. Not `''`, which Base UI clears to. */
const EVERY_OUTLET = 'every-outlet';
const EVERY_MACHINE = 'every-machine';
const EVERY_STATUS = 'every-status';

const STATUS_ITEMS = [
  { label: 'Standing and reversed', value: EVERY_STATUS },
  { label: 'Standing only', value: 'false' },
  { label: 'Reversed only', value: 'true' },
] as const;

const COLUMNS = 5;

export function MachineBills() {
  const client = useApiClient();
  const [searchParams] = useSearchParams();

  const importId = searchParams.get('importId');

  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));
  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [machineId, setMachineId] = useState<string>(EVERY_MACHINE);
  const [reversed, setReversed] = useState<string>(EVERY_STATUS);
  const [trail, setTrail] = useState<CursorTrail>(FIRST_PAGE);
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [machines, setMachines] = useState<readonly BillingMachineResource[]>([]);

  const problem = importId === null ? periodProblem(period, systemClock) : null;
  const cursor = currentCursor(trail);
  const complete =
    importId !== null || (period.from !== '' && period.to !== '' && problem === null);

  const query = useMemo(
    () => ({
      ...(importId === null ? { from: period.from, to: period.to } : { importId }),
      outletId: outletId === EVERY_OUTLET ? undefined : outletId,
      billingMachineId: machineId === EVERY_MACHINE ? undefined : machineId,
      reversed: reversed === EVERY_STATUS ? undefined : (reversed as 'true' | 'false'),
    }),
    [importId, period.from, period.to, outletId, machineId, reversed],
  );

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Bounded by the park; retired outlets included, since a bill outlives them.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // Costs the filter and the names, never the list.
        if (live) setOutlets([]);
      });

    // Bounded by the park too — a handful of machines.
    collectPages<BillingMachineResource>((offset, limit) =>
      client.request(BillingMachinesController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setMachines(all);
      })
      .catch(() => {
        // Costs the filter, never the list.
        if (live) setMachines([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (!complete) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(MachineBillsController_list_v1, {
        query: { ...query, cursor: cursor ?? undefined },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({
            kind: 'loaded',
            items: answer.items,
            total: answer.total,
            nextCursor: answer.nextCursor,
          });
        }
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, query, cursor, complete, reloads]);

  /** A cursor belongs to one result set; a changed filter starts again. */
  const changeFilter = (apply: () => void) => {
    apply();
    setTrail(FIRST_PAGE);
  };

  const outletNames = useMemo(
    () => new Map(outlets.map((outlet) => [outlet.id, outlet.name])),
    [outlets],
  );

  const outletItems = useMemo(
    () => [
      { label: 'Every outlet', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const machineItems = useMemo(
    () => [
      { label: 'Every machine', value: EVERY_MACHINE },
      ...machines.map((machine) => ({
        label: `${machine.name} (${machine.machineCode})`,
        value: machine.id,
      })),
    ],
    [machines],
  );

  const items = state.kind === 'loaded' ? state.items : [];
  const forbidden = state.kind === 'failed' && isForbidden(state.error);

  return (
    <Screen
      title="Machine bills"
      description="Bills printed on the ride and activity billing machines and imported from their files. Each is a cash sale on its own date at the outlet the machine was attached to. A reversed bill stays listed, marked."
      aside={
        <>
          {importId === null ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="machine-bills-from" className="text-sm text-muted-foreground">
                  From
                </Label>
                <DayPicker
                  id="machine-bills-from"
                  value={period.from}
                  emptyLabel="Choose a day"
                  format={formatBusinessDate}
                  fromDate={fromCalendarDate}
                  toDate={toCalendarDate}
                  onValueChange={(next) => {
                    changeFilter(() => {
                      setPeriod((held) => ({ ...held, from: next }));
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="machine-bills-to" className="text-sm text-muted-foreground">
                  To
                </Label>
                <DayPicker
                  id="machine-bills-to"
                  value={period.to}
                  emptyLabel="Choose a day"
                  format={formatBusinessDate}
                  fromDate={fromCalendarDate}
                  toDate={toCalendarDate}
                  onValueChange={(next) => {
                    changeFilter(() => {
                      setPeriod((held) => ({ ...held, to: next }));
                    });
                  }}
                />
              </div>

              {problem !== null && (
                <p role="status" className="text-sm text-destructive">
                  {problem}
                </p>
              )}
            </>
          ) : (
            <ScreenFact label="One upload">
              Only the bills this import created, on whatever days they fall.
              <br />
              <Link
                to={paths.machineBills}
                onClick={() => {
                  setTrail(FIRST_PAGE);
                }}
                className="text-sm text-muted-foreground underline decoration-dotted underline-offset-4 hover:text-foreground"
              >
                Show every bill instead
              </Link>
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="machine-bills-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  setOutletId(next ?? EVERY_OUTLET);
                });
              }}
            >
              <SelectTrigger
                id="machine-bills-outlet"
                aria-label="Outlet"
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outletItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="machine-bills-machine" className="text-sm text-muted-foreground">
              Machine
            </Label>
            <Select
              value={machineId}
              items={machineItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  setMachineId(next ?? EVERY_MACHINE);
                });
              }}
            >
              <SelectTrigger
                id="machine-bills-machine"
                aria-label="Machine"
                disabled={machines.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {machineItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="machine-bills-status" className="text-sm text-muted-foreground">
              Status
            </Label>
            <Select
              value={reversed}
              items={STATUS_ITEMS}
              onValueChange={(next) => {
                changeFilter(() => {
                  setReversed(next ?? EVERY_STATUS);
                });
              }}
            >
              <SelectTrigger
                id="machine-bills-status"
                aria-label="Status"
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {STATUS_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {state.kind === 'loaded' && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{state.total}</span>{' '}
              <span className="text-muted-foreground">{state.total === 1 ? 'bill' : 'bills'}</span>
            </ScreenFact>
          )}
        </>
      }
    >
      <ScreenTable label="Machine bills" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-36">Trading day</TableHead>
            <TableHead className="w-44">Receipt</TableHead>
            <TableHead className="w-56">Machine</TableHead>
            <TableHead className="w-40">Outlet</TableHead>
            <TableHead className="w-32 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {forbidden ? (
              <EmptyState
                title="This account cannot read the machine bills"
                description="Imported bills are the owner's and the manager's. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : (
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (items.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No imported bills"
                description={
                  importId === null
                    ? 'No machine bill matches these filters between these two days. Widen the period, or upload the machine’s file.'
                    : 'This upload created no bills — every bill in it was already imported, or refused.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {items.map((bill) => (
                <BillRow
                  key={bill.saleId}
                  bill={bill}
                  outletName={outletNames.get(bill.outletId) ?? null}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && (
        <CursorPager
          trail={trail}
          rows={items.length}
          nextCursor={state.nextCursor}
          onTrailChange={setTrail}
          label="machine bills"
          noun="bill"
        />
      )}
    </Screen>
  );
}

function BillRow({ bill, outletName }: { bill: MachineBillResource; outletName: string | null }) {
  const reversed = bill.reversal !== null;
  const quiet = reversed ? 'text-muted-foreground' : 'text-foreground';

  return (
    <TableRow>
      <TableCell className="align-top numeric">{formatBusinessDate(bill.businessDate)}</TableCell>

      <TableCell className="align-top whitespace-normal">
        <div className="flex flex-wrap items-center gap-2">
          {/* Built from the sale id: a receipt number is the machine's own
              counter and two machines print the same one. */}
          <Link
            to={machineBillPath(bill.saleId)}
            aria-label={`The record for bill ${bill.receiptNumber}`}
            className={`rounded-sm font-mono numeric underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30 ${quiet}`}
          >
            {bill.receiptNumber}
          </Link>
          {bill.revision > 1 && <Badge variant="secondary">Revision {String(bill.revision)}</Badge>}
          {reversed && <Badge variant="outline">Reversed</Badge>}
        </div>
      </TableCell>

      <TableCell className="align-top whitespace-normal">
        {bill.machineName}
        <br />
        <span className="font-mono text-xs text-muted-foreground numeric">{bill.machineCode}</span>
      </TableCell>

      <TableCell className="align-top">
        {outletName ?? <span className="font-mono text-xs numeric">{bill.outletId}</span>}
      </TableCell>

      <TableCell className="text-right align-top font-mono numeric">
        <span className={reversed ? 'text-muted-foreground line-through' : ''}>
          {formatPaise(bill.totalPaise)}
        </span>
      </TableCell>
    </TableRow>
  );
}
